import classNames from 'classnames';
import React, { FC, MouseEvent, useCallback, useState } from 'react';

import { parseCallback, useNativeEventListener } from '@eika-infrastruktur/mobilbank-lib';
import { FocusContainer } from '@eika/focus-container';
import { useViewType, ViewType } from '@eika/hooks';

import { MenuProps } from './MenuTypes';
import DesktopMenu from './desktopMenu/DesktopMenu';
import NativeMenu from './nativeMenu/NativeMenu';

import './style.scss';

const Menu: FC<MenuProps> = ({
    items = [],
    nativeItems = [],
    onMenuOpen,
    isNative = false,
    logout,
    logoutButtonText,
    bankLink = null,
}) => {
    const [open, setOpen] = useState<boolean>(false);
    const [focusLastElement, setFocusToLastElement] = useState(false);
    const viewType = useViewType();

    const [menuButtonRef, setMenuButtonRef] = useState<HTMLButtonElement | null>(null);
    const toggleMenu = useCallback(() => {
        if (open && menuButtonRef) {
            setFocusToLastElement(false);
            menuButtonRef.focus();
        }
        onMenuOpen?.(!open);
        setOpen(!open);
    }, [onMenuOpen, open, menuButtonRef]);

    const onClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        toggleMenu();
    };

    useNativeEventListener(
        useCallback(
            (event) => {
                const message = parseCallback(event.data);
                if (message?.action === 'BACK_TO_WEB') {
                    if (open) {
                        toggleMenu();
                    }
                }
            },
            [open, toggleMenu],
        ),
    );

    const keyDownOnMenuButton = useCallback(
        (event: React.KeyboardEvent<HTMLButtonElement>) => {
            const openKeyboardKeys = ['ArrowDown', 'Space', 'Enter', 'ArrowUp'];
            if (!open) {
                if (event.key in openKeyboardKeys) {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(true);
                    setFocusToLastElement(event.key === 'ArrowUp');
                }
            }
        },
        [open],
    );
    const menuLabel = open ? 'Lukk' : 'Meny';
    const menuScreenReaderLabel = open ? 'Lukk meny' : 'Åpne meny';
    const hideMenu = !items?.length && viewType !== ViewType.mobile && !isNative;

    return (
        <nav className={classNames('menu', { 'menu--open': open }, { 'native-menu': isNative })}>
            <FocusContainer
                entry={open}
                onExit={toggleMenu}
                useExtraKeyboardFocusCommands
                focusLastElementFirst={focusLastElement}
                className="menu-wrapper"
            >
                <button
                    className={classNames('menu__button', { no_menu_items: hideMenu }, 'sgw-button sgw-button-common')}
                    type="button"
                    onClick={onClick}
                    aria-haspopup="menu"
                    id="menu-button"
                    aria-expanded={open ? true : undefined}
                    aria-label={menuScreenReaderLabel}
                    onKeyDown={keyDownOnMenuButton}
                    ref={(el) => setMenuButtonRef(el)}
                >
                    <span className="menu-label">{menuLabel}</span>
                    <i />
                </button>
                {open && (
                    <>
                        {isNative ? (
                            <NativeMenu nativeItems={nativeItems} toggleMenu={toggleMenu} />
                        ) : (
                            <DesktopMenu
                                items={items}
                                logout={logout}
                                logoutButtonText={logoutButtonText}
                                bankLink={bankLink}
                            />
                        )}
                    </>
                )}
            </FocusContainer>
        </nav>
    );
};

export default Menu;
